import CustomModal, { BtnState } from "shared-components/modal";
import TableRowLabelValue from "shared-components/table-row-label-value";
import { LabelValueObject } from "types/label-value-object";
import { Maybe, SpendGroupRoster, SpendInvoice } from "graphql/generated";
import { DateFormatSupported, FormatDate } from "helpers/format-date";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_INVOICE } from "graphql/mutations/invoice";
import ToastContext from "context/toast-context";
import DatePicker from "shared-components/date-picker";
import { isNullOrEmpty } from "helpers/null-or-empty";
import { getDatePickerValue, setDatePickerValue } from "helpers/date-picker";

type EditDueDateProps = {
  editDueDateOpen: boolean;
  editDueDateToggle: () => void;
  participant: SpendGroupRoster;
  invoice: Maybe<SpendInvoice>;
};
function EditDueDate({
  editDueDateOpen,
  editDueDateToggle,
  participant,
  invoice,
}: EditDueDateProps) {
  const prepData = (
    participant: SpendGroupRoster,
    invoice: Maybe<SpendInvoice>
  ) => {
    let data: LabelValueObject[] = [];
    data.push({ key: "Invoice", value: invoice?.description ?? "" });
    data.push({
      key: "Group",
      value: participant.group?.name ?? "",
    });
    data.push({
      key: "Current Due Date",
      value: FormatDate(invoice?.dueDate ?? "", DateFormatSupported.Numbers),
    });
    return data;
  };

  const Toast = useContext(ToastContext);
  const [newDate, setNewDate] = useState("");
  const [isBtnActive, setIsBtnActive] = useState(true);
  const [UpdateInvoice, { loading, data, error }] = useMutation(
    UPDATE_INVOICE,
    {
      refetchQueries: ["GetParticipantsDetails"],
    }
  );

  useEffect(() => {
    if (!loading && data) {
      Toast?.setToast({
        message: "Invoice due date updated",
        type: "success",
      });
      editDueDateToggle();
      setNewDate("");
      setIsBtnActive(true);
    } else if (!loading && error) {
      Toast?.setToast({
        message: "Error updating due date",
        type: "danger",
      });
      setIsBtnActive(true);
    }
    // eslint-disable-next-line
  }, [loading, data, error]);

  return (
    <CustomModal
      isOpen={editDueDateOpen}
      toggle={() => {
        setIsBtnActive(true);
        editDueDateToggle();
      }}
      title={"Edit Invoice Due Date"}
      btn1={{
        text: "Save Changes",
        btnStyle: "primary",
        btnState: isBtnActive ? BtnState.BASE : BtnState.DISABLED,
        onClick: () => {
          if (isNullOrEmpty(newDate)) {
            Toast?.setToast({
              message: "Missing Due Date",
              type: "danger",
            });
          } else {
            UpdateInvoice({
              variables: {
                spendInvoiceUpdateId: invoice?.id,
                input: {
                  dueDate: newDate,
                },
              },
            });
            setIsBtnActive(false);
          }
        },
      }}
      btn2={{
        text: "Cancel",
        btnStyle: "tertiary",
        onClick: () => {
          setIsBtnActive(true);
          editDueDateToggle();
          setNewDate("");
        },
      }}
      customStyle="lg:mt-10 lg:w-[1082px]"
    >
      <div className="modal-card mb-5">
        <table className="w-full lg:w-auto">
          <tbody>
            {prepData(participant, invoice).map((p, idx) => {
              return (
                <TableRowLabelValue
                  key={idx}
                  label={p.key}
                  value={p.value.toString()}
                  className={`lg:ml-24 mt-2 ${p.className}`}
                  customContainerStyle={p.customContainerStyle}
                />
              );
            })}
          </tbody>
        </table>
        <div className="mt-2">
          <DatePicker
            label={"New Due Date"}
            date={getDatePickerValue(newDate ?? "")}
            setDate={(e) => {
              const newDate = setDatePickerValue(e);
              setNewDate(newDate);
            }}
          />
        </div>
      </div>
    </CustomModal>
  );
}

export default EditDueDate;
